import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useProjectRetroContext } from '../../context';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { theme } from 'config/theme';
import useFetchEventSource from '@buildwise/pages/search/hooks/useFetchEventSource';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PYTHON_SERVICE_ENDPOINTS, PYTHON_SERVICE_URL } from '@buildwise/libs/services';
import useUserData from 'hooks/useUserData';
import { AnswerContainer } from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/PromptHistory';
import WriteWithAISkeleton from '@buildwise/libs/posts/components/WriteWithAI/WriteWithAISkeleton';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import CheckFillIcon from 'remixicon-react/CheckFillIcon';
import ClipboardLineIcon from 'remixicon-react/ClipboardLineIcon';
import { formatNumberSequence } from '@buildwise/libs/helpers';
import SummaryReferences from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/SummaryReferences';

function RetroSummarizedResults() {
  const [openTooltip, setOpenTooltip] = useState(false);
  const { companyId } = useUserData();
  const {
    state: { articleIds },
    dispatch,
  } = useProjectRetroContext();
  const {
    fetchSSE,
    response: { loading, isStreaming, genAIResponse, error, sourceRefsMetaData },
  } = useFetchEventSource();
  const answerContainerRef = useRef<HTMLDivElement | null>(null);
  const [metaDataOrder, setMetaDataOrder] = useState<
    { originalValue: number; orderValue: number }[]
  >([]);

  const initializeStreaming = () => {
    try {
      const payload = {
        companyId,
        articleIds,
      };
      const _url = `${PYTHON_SERVICE_URL}${PYTHON_SERVICE_ENDPOINTS.GENERATE_RETROSPECTIVES_ARTICLE_SUMMARY}`;
      fetchSSE(_url, payload);
    } catch (error) {
      console.log('RETROSPECTIVE_SUMMARIZE_ERROR', error);
    }
  };

  useEffect(() => {
    initializeStreaming();
  }, []);

  useEffect(() => {
    if (answerContainerRef.current) {
      const allCitationElements = answerContainerRef.current.querySelectorAll('.citation');
      const formattedOrder = formatNumberSequence(
        Array.from(allCitationElements).map((element) => Number(element.id) || 0),
      );
      setMetaDataOrder?.(formattedOrder);
    }
  }, [answerContainerRef.current, genAIResponse]);

  const copyResponse = useCallback(async () => {
    try {
      if (answerContainerRef?.current?.innerHTML) {
        setOpenTooltip(true);
        const htmlContent = answerContainerRef.current.innerHTML;
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([answerContainerRef.current.innerText], { type: 'text/plain' }),
          }),
        ]);

        setTimeout(() => {
          setOpenTooltip(false);
        }, 1200);
      }
    } catch (error) {
      console.warn('COPY_RETROSPECTIVES_SUMMARY_ERROR', error);
    }
  }, [answerContainerRef.current]);

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" height="100%">
      <StickyContainer>
        <Box display="flex" justifyContent={'flex-end'} p={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={() => {
                dispatch({ payload: { isOpenedLessonForm: false, articleIds: [] } });
              }}
            >
              <CloseLineIcon color={theme.palette.custom.paleSky} />
            </IconButton>
          </Box>
        </Box>
        <Divider />
      </StickyContainer>
      <Box p={2} sx={{ height: '100%', overflow: 'auto' }}>
        {genAIResponse || loading ? (
          <>
            <AnswerContainer
              ref={answerContainerRef}
              width="100% !important"
              padding="16px !important"
              sx={{ borderRadius: '8px', mr: 0 }}
            >
              {loading ? (
                <WriteWithAISkeleton />
              ) : isStreaming || genAIResponse ? (
                <>
                  <RenderLLMMarkdown
                    llmResponse={genAIResponse || ''}
                    metaData={sourceRefsMetaData}
                    metaDataOrder={metaDataOrder}
                  />
                  {metaDataOrder.length > 0 &&
                    sourceRefsMetaData &&
                    sourceRefsMetaData?.length > 0 && (
                      <SummaryReferences
                        references={sourceRefsMetaData}
                        metaDataOrder={metaDataOrder}
                      />
                    )}
                </>
              ) : null}
            </AnswerContainer>
            {!isStreaming && !loading && (
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Tooltip open={openTooltip} placement="top" title={<Typography>Copied</Typography>}>
                  <IconButton onClick={copyResponse}>
                    {openTooltip ? <CheckFillIcon size={18} /> : <ClipboardLineIcon size={18} />}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </>
        ) : error ? (
          <Typography>Summarization failed due to an error. Please try again later.</Typography>
        ) : null}
      </Box>
    </Box>
  );
}
const StickyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));
export default RetroSummarizedResults;
